/**
 * Stage tablet queue.
 *
 * One WebSocket on /stage. HTTP results update the list immediately;
 * socket events keep other tablets in sync.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { PrayerRequest } from '@/types';
import { useWebSocket, WSEventType, WSMessage } from './useWebSocket';

export interface StageQueueItem {
  id: string;
  prayerRequestId: string;
  createdAt: string;
  prayer: PrayerRequest;
}

interface StageQueueListResponse {
  items: StageQueueItem[];
}

interface UseStageQueueOptions {
  token?: string;
  enabled?: boolean;
}

export function useStageQueue({ token, enabled = true }: UseStageQueueOptions) {
  const [items, setItems] = useState<StageQueueItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sendingIds, setSendingIds] = useState<Set<string>>(() => new Set());
  const [removingIds, setRemovingIds] = useState<Set<string>>(() => new Set());
  const [isClearing, setIsClearing] = useState(false);
  const backupRef = useRef<StageQueueItem[] | null>(null);

  const fetchQueue = useCallback(async () => {
    if (!enabled) return;
    try {
      const response = await api.get<StageQueueListResponse>('/stage-queue');
      if (response.error) {
        setError(response.error);
        return;
      }
      setItems(response.data?.items ?? []);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load the stage queue');
    } finally {
      setIsLoading(false);
    }
  }, [enabled]);

  const handleMessage = useCallback((message: WSMessage) => {
    switch (message.event) {
      case WSEventType.STAGE_ITEM_ADDED: {
        const item = message.data as StageQueueItem;
        if (!item?.prayerRequestId && !item?.prayer?.id) return;
        const prayerId = item.prayerRequestId || item.prayer.id;
        setItems((prev) => {
          if (prev.some((row) => row.prayerRequestId === prayerId || row.prayer.id === prayerId)) {
            return prev.map((row) =>
              row.prayerRequestId === prayerId || row.prayer.id === prayerId ? item : row
            );
          }
          return [...prev, item];
        });
        break;
      }
      case WSEventType.STAGE_ITEM_REMOVED: {
        const removed = message.data as { prayerRequestId?: string };
        if (!removed.prayerRequestId) return;
        setItems((prev) =>
          prev.filter(
            (row) =>
              row.prayerRequestId !== removed.prayerRequestId &&
              row.prayer.id !== removed.prayerRequestId
          )
        );
        break;
      }
      case WSEventType.STAGE_QUEUE_CLEARED:
        setItems([]);
        break;
      default:
        break;
    }
  }, []);

  const { isConnected } = useWebSocket({
    token,
    rooms: ['moderators'],
    onMessage: enabled ? handleMessage : undefined,
  });

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    void fetchQueue();
  }, [enabled, fetchQueue, token]);

  useEffect(() => {
    if (!enabled || isConnected) return;
    const id = window.setInterval(() => {
      void fetchQueue();
    }, 10_000);
    return () => window.clearInterval(id);
  }, [enabled, isConnected, fetchQueue]);

  const sendToScreen = useCallback(async (prayerId: string): Promise<boolean> => {
    setSendingIds((prev) => new Set(prev).add(prayerId));
    setError(null);
    try {
      const response = await api.post<StageQueueItem>(`/stage-queue/${prayerId}/send-to-screen`);
      if (response.error) {
        setError(response.error);
        return false;
      }
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send to screen');
      return false;
    } finally {
      setSendingIds((prev) => {
        const next = new Set(prev);
        next.delete(prayerId);
        return next;
      });
    }
  }, []);

  const removeFromStage = useCallback(async (prayerId: string): Promise<boolean> => {
    setRemovingIds((prev) => new Set(prev).add(prayerId));
    setError(null);
    setItems((prev) => {
      backupRef.current = prev;
      return prev.filter(
        (row) => row.prayerRequestId !== prayerId && row.prayer.id !== prayerId
      );
    });
    try {
      const response = await api.delete(`/stage-queue/${prayerId}`);
      if (response.status === 404) {
        return true;
      }
      if (response.error) {
        if (backupRef.current) setItems(backupRef.current);
        setError(response.error);
        return false;
      }
      return true;
    } catch (err) {
      if (backupRef.current) setItems(backupRef.current);
      setError(err instanceof Error ? err.message : 'Failed to remove from stage');
      return false;
    } finally {
      backupRef.current = null;
      setRemovingIds((prev) => {
        const next = new Set(prev);
        next.delete(prayerId);
        return next;
      });
    }
  }, []);

  const clearStage = useCallback(async (): Promise<boolean> => {
    setIsClearing(true);
    setError(null);
    setItems((prev) => {
      backupRef.current = prev;
      return [];
    });
    try {
      const response = await api.post<{ removed: number }>('/stage-queue/clear');
      if (response.error) {
        if (backupRef.current) setItems(backupRef.current);
        setError(response.error);
        return false;
      }
      return true;
    } catch (err) {
      if (backupRef.current) setItems(backupRef.current);
      setError(err instanceof Error ? err.message : 'Failed to clear the stage queue');
      return false;
    } finally {
      backupRef.current = null;
      setIsClearing(false);
    }
  }, []);

  return {
    items,
    isLoading,
    error,
    isConnected,
    sendingIds,
    removingIds,
    isClearing,
    sendToScreen,
    removeFromStage,
    clearStage,
    refresh: fetchQueue,
  };
}
